import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { AccordionDetails } from '@mui/material';
import { withStyles } from '@mui/styles';
import MuiAccordion from '@mui/material/Accordion';
import IconButton from '@mui/material/IconButton';
import InputText from '@/components/UI/InputText/InputText';
import { getTitleWithoutDoormanText } from '@/helpers/utils';
import useDetectLanguage from '@/hooks/useDetectLanguage';
import { getCampaignInfo } from '@/redux/Campaign/campaign.reducer';
import RegularTabContent from './RegularTabContent/RegularTabContent';
import VehiclesTabContent from './VehiclesTabContent/VehiclesTabContent';
import { okNokFields } from '../translations-utils';
import styles from './TranslationsAccordion.module.scss';

const Accordion = withStyles({
  root: {
    border: '1px solid rgba(0, 0, 0, .125)',
    boxShadow: 'none',
    '&:not(:last-child)': {
      borderBottom: 0,
    },
    '&:before': {
      display: 'none',
    },
    '&$expanded': {
      margin: 'auto',
    },
  },
  expanded: {},
})(MuiAccordion);

const TranslationsAccordion = (props) => {
  const { tab, translations, defaultLanguage, translationLang, onInputChange = () => { }, translitonsValidations, disabled,
    validationActive, campaignInfo, isExpandedAll, index, onExpand } = props;
  const [expanded, setExpanded] = useState(false);
  const [vehiclesTranslations, setVehiclesTranslations] = useState([]);
  const [groupVehiclesTranslations, setGroupVehiclesTranslations] = useState([]);
  const { t } = useTranslation();
  const language = defaultLanguage === 'EN' ? 'DE' : 'EN';
  const detectLanguage = useDetectLanguage();
  const isDoorman = campaignInfo?.doormanSelected;

  const calculateVehiclesComments = (translations?.translatablePropertiesByPlant || []).reduce((accumulator, properties) => {
    const fieldsCount = properties.translatableProperties.filter((field) => field.translations[defaultLanguage]).length;
    const groupFieldsCount = properties?.translatablePropertiesByGroup
      .reduce((acc, { translatableProperties }) => (acc + translatableProperties.filter((field) => field.translations[defaultLanguage]).length), 0);

    return {
      ...accumulator,
      [properties.plant.wkzNumber]: (fieldsCount + groupFieldsCount),
      total: (accumulator.total + fieldsCount + groupFieldsCount),
    };
  }, { total: 0 });

  const calculateOthersComments = { total: Object.values(translations?.translatableProperties || {}).filter((field) => field.translations[defaultLanguage] !== '').length };

  const calculateComments = tab === 'AFFECTED_VEHICLES'
    ? calculateVehiclesComments
    : calculateOthersComments;

  const affectedPlants = (option) => (translations?.translatablePropertiesByPlant || [])
    .find(({ plant }) => plant.wkzNumber === option?.wkzNumber);

  const selectPlantTranslations = (option) => {
    const plantSelectedTranslations = affectedPlants(option)?.translatableProperties;
    const plantSelectedGroupsTranslations = affectedPlants(option)?.translatablePropertiesByGroup;
    setVehiclesTranslations(plantSelectedTranslations || []);
    setGroupVehiclesTranslations(plantSelectedGroupsTranslations || []);
  };

  const validationInvalid = () => {
    let invalid = false;

    if (tab === 'AFFECTED_VEHICLES') {
      invalid = translitonsValidations?.[language]?.affectedVehicles?.invalid;
    } else {
      Object.values(translations).forEach((content) => {
        Object.keys(content).forEach((comment) => {
          if (content[comment].translations[defaultLanguage] !== '') {
            invalid = invalid || translitonsValidations?.[language]?.[comment]?.invalid;
          }
        });
      });
    }

    return invalid && validationActive ? 'error' : '';
  };

  const translate = (field = '', lang = translationLang, id, editable) => {
    if (tab === 'AFFECTED_VEHICLES' && field === 'codeRuleComment') {
      return vehiclesTranslations?.find(({ uuid }) => uuid === id)?.translations[lang] || '';
    }

    if (tab === 'AFFECTED_VEHICLES' && !okNokFields.includes(field)) {
      return vehiclesTranslations?.find(({ code }) => code === field)?.translations[lang] || '';
    }

    if (tab === 'AFFECTED_VEHICLES' && (okNokFields.includes(field))) {
      const groupFounded = groupVehiclesTranslations?.find(({ translatableProperties }) => translatableProperties?.find(({ uuid }) => uuid === id));

      return groupFounded?.translatableProperties?.find(({ uuid }) => uuid === id)?.translations?.[lang] || '';
    }

    if (field === 'campaignTitle' && editable && isDoorman) {
      return getTitleWithoutDoormanText(translations?.translatableProperties?.[field]?.translations[lang] || '', lang, true);
    }

    return translations?.translatableProperties?.[field]?.translations[lang] || '';
  };

  const getStartAdorment = (comment, lang, editable) => ((editable && isDoorman && comment === 'campaignTitle')
    ? `${t('campaignRequests.doormanFixedTitle', { lng: lang.toLowerCase() })}:`
    : null);

  const hadMaxLength = (value = '', length) => value.length === length;

  const invalid = (comment, length, uuid, plantSelected, value) => {
    if (tab === 'AFFECTED_VEHICLES') {
      return translitonsValidations?.[language]?.affectedVehicles?.[plantSelected?.wkzNumber]
        ?.[okNokFields.includes(comment) ? uuid : comment] || hadMaxLength(value, length);
    }

    return translitonsValidations?.[language]?.[comment]?.invalid || hadMaxLength(value, length);
  };

  const contentComment = (comment, length, uuid, plantSelected) => {
    const value = translate(comment, translationLang, uuid, !disabled);

    return (
      <>
        <div className={styles.TwoColumns}>
          <p>{t('translations.translateTo', { lang: t(`languages.${translationLang}`) })}</p>
          <InputText
            disabled={disabled}
            isMandatory
            invalid={invalid(comment, length, uuid, plantSelected, value)}
            multiline
            maxLength={length}
            rows={[...okNokFields, 'campaignTitle'].includes(comment) ? 1 : 5}
            id={`translation-${comment}`}
            label={t([`${tab.toLowerCase().split('_').pop()}.${comment}`, `${tab}.${comment}`])}
            value={value}
            handleChange={(ev) => onInputChange(ev.currentTarget.value, tab, comment, plantSelected, uuid)}
            onBlur={(ev) => detectLanguage(ev.target.value, true)}
            startAdornment={getStartAdorment(comment, translationLang, !disabled)}
            helperText={[
              ...((hadMaxLength(value, length))
                ? [t('validations.maxLength', { maxLength: length })]
                : []),
            ]}
          />
        </div>
        <div className={styles.TwoColumns}>
          <p>{t('translations.commentEntries')}</p>
          <InputText
            disabled
            multiline
            rows={[...okNokFields, 'campaignTitle'].includes(comment) ? 1 : 5}
            id={`translation-ref-${comment}`}
            label={t([`${tab.toLowerCase().split('_').pop()}.${comment}`, `${tab}.${comment}`])}
            value={translate(comment, defaultLanguage.toUpperCase(), uuid)}
          />
        </div>
      </>
    );
  };

  useEffect(() => {
    if (isExpandedAll.action) {
      setExpanded(true);
    } else {
      setExpanded(false);
    }
  }, [isExpandedAll]);

  const expanding = () => {
    setExpanded(!expanded);
    onExpand();
  };

  return (
    <Accordion expanded={expanded} id="translations-accordion" index={index}>
      <div className={styles.AccordionHeader} onClick={() => expanding()}>
        <div className={`${styles.Title} ${validationInvalid()}`}>{t(`navTab.${tab}`)} ({calculateComments.total} {t('translations.comments')})</div>
        <IconButton data-qa={`expand-button-${tab.toLowerCase()}`} size="small">
          {expanded && <i className={`di icon-pfeil-chevron-oben ${styles.Icon} `} />}
          {!expanded && <i className={`di icon-pfeil-chevron-unten ${styles.Icon} `} />}
        </IconButton>
      </div>
      <AccordionDetails>
        {tab === 'AFFECTED_VEHICLES' && (
          <VehiclesTabContent
            tab={tab}
            language={language}
            translations={translations}
            defaultLanguage={defaultLanguage}
            contentComment={contentComment}
            vehiclesTranslations={vehiclesTranslations}
            groupVehiclesTranslations={groupVehiclesTranslations}
            validations={translitonsValidations}
            selectPlantTranslations={selectPlantTranslations}
            fieldsNumber={calculateComments}
            validationActive={validationActive}
          />
        )}
        {tab !== 'AFFECTED_VEHICLES' && (
          <RegularTabContent
            tab={tab}
            translations={translations}
            defaultLanguage={defaultLanguage}
            contentComment={contentComment}
          />
        )}
      </AccordionDetails>
    </Accordion>
  );
};

const mapStateToProps = (state) => ({
  campaignInfo: getCampaignInfo(state),
});

export default connect(mapStateToProps)(TranslationsAccordion);
